import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { TranslatePipe } from "@ngx-translate/core";
import { uniq } from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { EventService } from "~app/common/services/cqs/event.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { TplFilterApplied, TplFilterReset } from "../../events/tplEventHandler";
import { ITplArbetsorder } from "../../models/dtos";

import { TplStateService } from "../../state/tpl-state.service";
import { TplAffarsformFilterComponent } from "./tpl-affarsform-filter/tpl-affarsform-filter.component";
import { TplAnskaffningsformFilterComponent } from "./tpl-anskaffningsform-filter/tpl-anskaffningsform-filter.component";
import { TplAvstandFilterComponent } from "./tpl-avstand-filter/tpl-avstand-filter.component";
import { TplDestineradFilterComponent } from "./tpl-destinerad-filter/tpl-destinerad-filter.component";
import { TplDestineringsomradeFilterComponent } from "./tpl-destineringsomrade-filter/tpl-destineringsomrade-filter.component";
import { TplFilterContext } from "./tpl-filter-context";
import { ITplFilterTypes } from "./tpl-filter.models";
import { TplFritextFilterComponent } from "./tpl-fritext-filter/tpl-fritext-filter.component";
import { TplHandelssortimentFilterComponent } from "./tpl-handelssortiment-filter/tpl-handelssortiment-filter.component";
import { TplLageralderFilterComponent } from "./tpl-lageralder-filter/tpl-lageralder-filter.component";
import { TplMaskinlagFilterComponent } from "./tpl-maskinlag-filter/tpl-maskinlag-filter.component";
import { TplMottagningsplatsFilterComponent } from "./tpl-mottagningsplats-filter/tpl-mottagningsplats-filter.component";
import { TplNasfranvagFilterComponent } from "./tpl-nasfranvag-filter/tpl-nasfranvag-filter.component";
import { TplProduktionsomradeFilterComponent } from "./tpl-produktionsomrade-filter/tpl-produktionsomrade-filter.component";
import { TplProduktionsstatusFilterComponent } from "./tpl-produktionsstatus-filter/tpl-produktionsstatus-filter.component";
import { TplRedovisningshanvisningFilterComponent } from "./tpl-redovisningshanvisning-filter/tpl-redovisningshanvisning-filter.component";
import { TplSkordningstidFilterComponent } from "./tpl-skordningstid-filter/tpl-skordningstid-filter.component";
import { TplSkotningstidFilterComponent } from "./tpl-skotningstid-filter/tpl-skotningstid-filter.component";
import { TplTillganglighetFilterComponent } from "./tpl-tillganglighet-filter/tpl-tillganglighet-filter.component";
import { TplTransportforetagFilterComponent } from "./tpl-transportforetag-filter/tpl-transportforetag-filter.component";
import { TplTransporthandelseFilterComponent } from "./tpl-transporthandelse-filter/tpl-transporthandelse-filter.component";
import { TplTransporttilldeladFilterComponent } from "./tpl-transporttilldelad-filter/tpl-transporttilldelad-filter.component";
import { TplVaglagerFilterComponent } from "./tpl-vaglager-filter/tpl-vaglager-filter.component";
import { TplValbaraMottagningsplatserFilterComponent } from "./tpl-valbara-mottagningsplatser-filter/tpl-valbara-mottagningsplatser-filter.component";
import { TplVolymFilterComponent } from "./tpl-volym-filter/tpl-volym-filter.component";

@Component({
    selector: "rask-tpl-filter",
    templateUrl: "./tpl-filter.component.html",
    styleUrls: ["./tpl-filter.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        HeaderComponent,
        FormsModule,
        ReactiveFormsModule,
        TplFritextFilterComponent,
        TplTransportforetagFilterComponent,
        TplDestineradFilterComponent,
        TplTransporthandelseFilterComponent,
        TplTransporttilldeladFilterComponent,
        TplVaglagerFilterComponent,
        TplLageralderFilterComponent,
        TplNasfranvagFilterComponent,
        TplMottagningsplatsFilterComponent,
        TplValbaraMottagningsplatserFilterComponent,
        TplAvstandFilterComponent,
        TplVolymFilterComponent,
        TplHandelssortimentFilterComponent,
        TplMaskinlagFilterComponent,
        TplProduktionsstatusFilterComponent,
        TplAnskaffningsformFilterComponent,
        TplAffarsformFilterComponent,
        TplTillganglighetFilterComponent,
        TplProduktionsomradeFilterComponent,
        TplDestineringsomradeFilterComponent,
        TplSkotningstidFilterComponent,
        TplSkordningstidFilterComponent,
        TplRedovisningshanvisningFilterComponent,
        ButtonComponent,
        TranslatePipe
    ]
})
export class TplFilterComponent implements OnInit, OnDestroy {
    filterContext = new TplFilterContext();
    form = new UntypedFormGroup({});
    isLoaded = false;
    nrFiltereadeAos = null;
    source: ITplArbetsorder[];

    private readonly WIK = "Transportplanering.filter";
    private readonly eventService = inject(EventService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly tplStateService = inject(TplStateService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    get isTransportsattBransle(): boolean {
        return this.tplStateService.current.transportsatt.value.bransle;
    }

    applyFilter = () => {
        this.filterContext.flush();
        const matches = this.filterContext.matchAll(this.source);
        this.nrFiltereadeAos = matches.length;

        this.saveFilter();
        this.emitFilterApplied();

        const selectedHsId = this.filterContext.filterState.handelssortiment ?? [];
        const selectedMplId = this.filterContext.filterState.mottagningsplats ?? [];
        const selectedDsoId = this.filterContext.filterState.destineringsomrade ?? [];
        const isVaglagerFilterSelected = this.filterContext.isVaglagerActive();

        this.tplStateService.current.setSelectedHandelssortiment(selectedHsId);

        const mplid = uniq(selectedMplId.map(x => parseInt(x, 10)));

        this.tplStateService.current.setSelectedMottagningsplatser(mplid);
        this.tplStateService.current.setSelectedVaglager(isVaglagerFilterSelected);
        this.tplStateService.current.setSelectedDestineringsomraden(selectedDsoId);

        this.tplStateService.current.setCurrentArbetsordrar(matches);
        this.tplStateService.current.setSelected({ origin: "filter",
            selected: [] });
    };

    init() {
        this.isLoaded = false; // !IMPORTANT. Denna gör att alla filter initierar sig "hårt"

        setTimeout(() => {
            this.reset();
            this.webinstallningarService
                .getAsync<ITplFilterTypes>(this.WIK)
                .then((filters: ITplFilterTypes) => {
                    this.filterContext = new TplFilterContext(filters); // !IMPORTANT. FilterContext *måste* följa samma livcykel som alla filter på sidan
                    this.isLoaded = true;
                });
        });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit() {
        const state = this.tplStateService.current;

        state.arbetsordrar
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.source = this.tplStateService.current.getArbetsordrar();
                this.init();
            });
    }

    reset = (clearFilter = false) => {
        this.filterContext.clear();
        this.nrFiltereadeAos = null;

        if (clearFilter) {
            this.tplStateService.current.setCurrentArbetsordrar(this.source);
            this.tplStateService.current.setSelected({ origin: "filter",
                selected: [] });
            this.tplStateService.current.setSelectedHandelssortiment(null);
            this.tplStateService.current.setSelectedMottagningsplatser(null);
            this.tplStateService.current.setSelectedDestineringsomraden(null);
            this.tplStateService.current.setSelectedVaglager(null);

            this.filterContext.flush();
            // eslint-disable-next-line sonarjs/no-unused-vars, sonarjs/no-dead-store
            const matches = this.filterContext.matchAll(this.source);
            this.saveFilter();
            this.emitFilterReset();
        }
    };

    private emitFilterApplied() {
        const currentFilterCopy = ObjectUtils.deepCopy(this.filterContext.filterState ?? {}) as ITplFilterTypes;
        this.eventService.emit(new TplFilterApplied(currentFilterCopy));
    }

    private emitFilterReset() {
        this.eventService.emit(new TplFilterReset());
    }

    private saveFilter() {
        const filters = ObjectUtils.deepCopy(this.filterContext.filterState);
        this.webinstallningarService.set(this.WIK, filters);
    }
}
